/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { DATE, formatDate, formatDateUTC, getDiff, getDuration } from '../../utils/date';
import {
  companyTrackerUserRoles,
  portfolioTrackerUserRoles,
  UserFilterReportingLevel,
  UserRoles,
} from '../../constants/user';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { UserFilter } from './UserFilter';
import { useAppSelector } from '../../reducers';
import { escapeRegexCharacters } from '../../utils/string-format';
import { UserTableMenuButton } from './partials/UserTableMenuButton';
import { UserRolesDropdown } from './partials/UserRolesDropdown';
import './styles.scss';
import { RequestToJoinModal } from './RequestToJoinModal';
import { SelectOption } from '../../types/select';
import IconButton from '@g17eco/molecules/button/IconButton';
import { exportToExcel } from '@features/downloads/util/exportToExcel';
import { getInitiativesTreeMapById } from '@selectors/initiativeTree';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table, ColumnDef } from '@g17eco/molecules/table';
import { naturalSort } from '@utils/index';
import { UserInvitationInfoModal } from '@apps/company-tracker/components/user-invitation-info/UserInvitationInfoModal';
import { ManagementInvitation, OnboardingStatus } from '@g17eco/types/onboarding';
import { getRoleName } from '@utils/user';
import { UserTableUser } from './types';
import { ManagedOnHoverCard } from './partials/ManagedOnHoverCard';
import { RouteInterfaceMin } from '../../types/routes';

interface UserTableProps {
  handleReload: () => Promise<void>;
  users: UserTableUser[];
  toggleUserInvitationForm: () => void;
  isAddUserDisabled?: boolean;
  btnText?: string;
  filterReportingLevel?: UserFilterReportingLevel;
  handleReportingLevelChange: (newValue: any) => void;
  isDownloadable?: boolean;
  /** Controlled search; also makes card links pre-search the user. Local when unset */
  search?: string;
  onSearchChange?: (search: string) => void;
  hasOwner: boolean;
  baseRoute: RouteInterfaceMin;
}

const getRemainingTime = (expirationDate: string): string => {
  const diff = getDiff(expirationDate, new Date());

  if (diff <= 0) {
    return 'expired';
  }

  return getDuration(diff).humanize();
};

const getStatus = (user: UserTableUser): { status: string; tooltip?: string } => {
  if (user.isActivated) {
    const expiringPermission = user.user?.permissions?.find(p => p.expirationDate);
    if (expiringPermission?.expirationDate) {
      const remaining = getRemainingTime(expiringPermission.expirationDate);
      return {
        status: `Active (${remaining})`,
        tooltip: `Access expires ${formatDateUTC(expiringPermission.expirationDate, DATE.DEFAULT_SPACES)}`
      };
    }
    return { status: 'Active' };
  }

  if (user.isRequestToJoin) {
    return { status: 'Not Started' };
  }

  if (user.invitation?.expirationDate && new Date() >= new Date(user.invitation.expirationDate)) {
    return { status: 'Expired' };
  }

  return { status: 'Pending' };
}

export const UserTable = (props: UserTableProps) => {
  const {
    handleReload,
    users,
    toggleUserInvitationForm,
    isAddUserDisabled = false,
    btnText = 'Add user',
    filterReportingLevel,
    handleReportingLevelChange,
    isDownloadable = false,
    search,
    onSearchChange,
    hasOwner,
    baseRoute,
  } = props;

  const match = useRouteMatch<{ initiativeId?: string, portfolioId?: string }>();
  const location = useLocation();
  const history = useHistory();

  const initiativeId = match.params.initiativeId ?? match.params.portfolioId ?? '';
  const roles = match.params.portfolioId ? portfolioTrackerUserRoles : companyTrackerUserRoles;

  // The input and filter use a local draft; a controlled search is written back debounced
  const [searchText, setSearchText] = useState(search ?? '');
  const [prevSearch, setPrevSearch] = useState(search);
  if (search !== prevSearch) {
    setPrevSearch(search);
    setSearchText(search ?? '');
  }
  const isSearchControlled = search !== undefined;
  const debouncedSearchChange = useDebouncedCallback((value: string) => onSearchChange?.(value), 300);
  // Write a pending search before a reload remounts the table
  useEffect(() => () => {
    debouncedSearchChange.flush();
  }, [debouncedSearchChange]);
  const handleSearchChange = (value: string) => {
    setSearchText(value);
    debouncedSearchChange(value);
  };
  const [filteredUsers, setFilteredUsers] = useState<(UserTableUser[])>(users);
  const [requestToJoinModal, setRequestToJoinModal] = useState<ManagementInvitation | undefined>(undefined);
  const [onboarding, setOnboarding] = useState<UserTableUser | undefined>(undefined);

  const [filterStatus, setFilterStatus] = useState<'active' | 'pending' | undefined>();
  const [filterPermission, setFilterPermission] = useState<UserRoles | undefined>();
  const currentUserState = useAppSelector((state) => state.currentUser);

  const initiativeTree = useAppSelector(getInitiativesTreeMapById);

  const onboardingInitiativeId = onboarding?.invitation?.user?.permissions.find((p) =>
    initiativeTree.has(p.initiativeId),
  )?.initiativeId;

  useEffect(() => {
    if (users.length > 0 && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const id = searchParams.get('onboardingId');
      if (id) {
        const user = users.find(u => u.invitation?._id === id);
        if (user?.invitation && user.invitation.status === OnboardingStatus.NotStarted) {
          setRequestToJoinModal(user.invitation)
        }
        searchParams.delete('onboardingId');
        history.replace({ pathname: location.pathname, search: searchParams.toString() });
      }
    }
  }, [history, location.pathname, location.search, users]);

  React.useEffect(() => {
    const filterUsers = (user: UserTableUser) => {
      if (filterStatus) {
        if (filterStatus === 'active' && !user.isActivated) {
          return false;
        }
        if (filterStatus === 'pending' && user.isActivated) {
          return false;
        }
      }

      if (filterPermission) {
        if (!user.roles.includes(filterPermission)) {
          return false;
        }
      }

      if (searchText) {
        const escapedSearchText = escapeRegexCharacters(searchText.trim().toLowerCase());
        const searchTextRegex = new RegExp(escapedSearchText.replace(/ /g, '|'), 'g');
        if ((`${user.name}`.toLowerCase().match(searchTextRegex) || []).length > 0) {
          return true;
        }
        if ((`${user.email}`.toLowerCase().match(searchTextRegex) || []).length > 0) {
          return true;
        }
        return false;
      }
      return true;
    };
    setFilteredUsers(users.filter(filterUsers));
  }, [users, initiativeId, searchText, filterStatus, filterPermission]);

  const handlePermissionChange = (filter: SelectOption<UserRoles | undefined> | SelectOption<UserRoles | undefined>[]) => {
    if (Array.isArray(filter)) {
      return;
    }
    setFilterPermission(filter?.value ?? undefined)
  };

  const handleStatusChange = (filter: SelectOption<'active' | 'pending' | undefined> | SelectOption<'active' | 'pending' | undefined>[]) => {
    if (Array.isArray(filter)) {
      return;
    }
    setFilterStatus(filter?.value ?? undefined)
  };

  // Memoized so cells keep their state across renders, e.g. an open managed-on card
  const tableColumns = useMemo((): ColumnDef<UserTableUser>[] => {
    const isCurrentUserOrInherited = (user: UserTableUser) =>
      user.isInherited || user._id === currentUserState.data._id;
    return [
    {
      id: '_id',
      header: '',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: ({ row }) => {
        const user = row.original;
        if (user.isInherited) {
          return (
            <ManagedOnHoverCard
              levels={user.managedOn ?? []}
              managedOnSubsidiaries={user.managedOnSubsidiaries}
              // ?search= doesn't filter support users, so only the main table's links set it
              email={isSearchControlled ? user.email : undefined}
              baseRoute={baseRoute}
            />
          );
        }

        const isCurrentUser = user._id === currentUserState.data._id;
        if (isCurrentUser) {
          return (
            <SimpleTooltip text='You cannot modify your own permissions.'>
              <i className='fa-light fa fa-circle-info' color='secondary' />
            </SimpleTooltip>
          );
        }
        return (
          <UserTableMenuButton
            hasOwner={hasOwner}
            user={user}
            initiativeId={initiativeId}
            handleReload={handleReload}
            openResendActivationModal={setOnboarding}
          />
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      meta: {
        cellProps: {
          className: 'dont_translate',
        },
      },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <SimpleTooltip text={user.name}>
            <div
              style={{ maxWidth: '150px' }}
              className={`text-truncate ${isCurrentUserOrInherited(user) ? 'text-ThemeDisabled' : ''}`}
            >
              {user.name}
            </div>
          </SimpleTooltip>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email address',
      meta: {
        cellProps: {
          className: 'emailCol dont_translate',
        },
      },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <SimpleTooltip text={user.email}>
            <div
              className={`${
                isCurrentUserOrInherited(user) ? 'text-ThemeDisabled' : ''
              } hover-expand text-truncate dont_translate`}
            >
              {user.email}
            </div>
          </SimpleTooltip>
        );
      },
    },
    {
      accessorKey: 'roles',
      header: 'Permissions',
      cell: ({ row }) => {
        const user = row.original;
        if (!currentUserState.loaded) {
          return;
        }

        return (
          <UserRolesDropdown
            currentUser={currentUserState.data}
            user={user}
            initiativeId={initiativeId}
            roles={roles}
            handleReload={handleReload}
          />
        );
      },
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last login',
      sortingFn: (a, b) => naturalSort(a.original.lastLogin ?? '', b.original.lastLogin ?? ''),
      cell: ({ row }) => {
        const user = row.original;
        return (
          <span className={isCurrentUserOrInherited(user) ? 'text-ThemeDisabled' : ''}>
            {user.isRequestToJoin ? undefined : user.lastLogin ? formatDate(user.lastLogin, DATE.HUMANIZE) : 'never'}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      sortingFn: (a, b) => naturalSort(getStatus(a.original).status, getStatus(b.original).status),
      cell: ({ row }) => {
        const user = row.original;
        if (user.isRequestToJoin) {
          return (
            <Button
              className='text-nowrap p-0'
              color='link'
              size='sm'
              onClick={() => user.invitation && setRequestToJoinModal(user.invitation)}
            >
              View request
            </Button>
          );
        }

        const { status, tooltip } = getStatus(user);
        const statusContent = (
          <div className={`${isCurrentUserOrInherited(user) ? 'text-ThemeDisabled' : ''}`}>{status}</div>
        );

        if (tooltip) {
          return (
            <SimpleTooltip text={tooltip}>
              {statusContent}
            </SimpleTooltip>
          );
        }

        return statusContent;
      },
    },
    ];
  }, [baseRoute, currentUserState.data, currentUserState.loaded, handleReload, hasOwner, initiativeId,
    isSearchControlled, roles]);

  const noResults = filteredUsers.length === 0;
  let noUsersMessage = '';
  if (noResults) {
    noUsersMessage =
      filterPermission || filterStatus || searchText
        ? 'No users match your criteria'
        : 'You have no users assigned to this reporting level';
  }

  const handleDownload = () => {
    const headers = ['Name', 'Email', 'Invite date', 'Status', 'Subsidiary', 'Permissions'];
    const values = filteredUsers.reduce((rows, user) => {
      const invitedDate = user.invitedDate ?? user.invitation?.created;
      const formattedInvitedDate = invitedDate ? formatDateUTC(invitedDate, DATE.DEFAULT_DASHES) : '';
      const { status } = getStatus(user);

      const permissions = (user.isActivated ? user.user?.permissions : user.invitation?.user.permissions) ?? [];
      permissions.forEach((permission) => {
        const initiative = initiativeTree.get(permission.initiativeId);
        if (!initiative) {
          return;
        }

        rows.push([
          user.name ?? '',
          user.email ?? '',
          formattedInvitedDate,
          status,
          initiative.name,
          permission.permissions.map((permission) => getRoleName(permission)).join(', '),
        ]);
      });

      return rows;
    }, [] as string[][]);
    exportToExcel({ headers, values, fileName: 'users' });
  };

  return (
    <>
      <div className='d-flex align-items-center'>
        <div className='flex-grow-1'>
          <Input
            placeholder={'Search for user...'}
            onChange={(e) => handleSearchChange(e.target.value)}
            value={searchText} />
        </div>
        <div className='mx-2'>
          <UserFilter
            filterStatus={filterStatus}
            handleStatusChange={handleStatusChange}
            filterPermission={filterPermission}
            handlePermissionChange={handlePermissionChange}
            filterReportingLevel={filterReportingLevel}
            handleReportingLevelChange={handleReportingLevelChange}
            defaultOptionText='All Users'
          />
        </div>
        {isDownloadable ? (
          <IconButton
            icon='fal fa-file-excel'
            onClick={handleDownload}
            tooltip='Export users. Report respects filters and will export only users visible in below list.'
            className='mr-2'
          />
        ) : null}
        <div className='ml-auto'>
          <Button
            outline
            color='primary'
            className={'text-nowrap'}
            onClick={toggleUserInvitationForm}
            disabled={isAddUserDisabled}
            data-testid='add-user-btn'
          >
            <i className={'fa fa-plus mr-2'} />
            <span>{btnText}</span>
          </Button>
        </div>
      </div>

      <div className='mt-3 user-table-container'>
        <Table
          columns={tableColumns}
          data={filteredUsers}
          pageSize={10}
        />
        {noResults ? <BasicAlert type='secondary' className='mt-2'>{noUsersMessage}</BasicAlert> : null}
      </div>

      {requestToJoinModal ? (
        <RequestToJoinModal
          invitation={requestToJoinModal}
          handleReload={handleReload}
          toggle={() => setRequestToJoinModal(undefined)}
        />
      ) : null}

      {onboardingInitiativeId || initiativeId ? (
        <UserInvitationInfoModal
          onboardingId={onboarding?._id}
          initiativeId={onboardingInitiativeId || initiativeId}
          toggle={() => setOnboarding(undefined)}
        />
      ) : null}
    </>
  );
}

export default UserTable;
